import React, { useMemo, useState } from 'react';
import { Dices, User } from 'lucide-react';
import './SetupScreen.css';

export type ProfileChoice =
  | { type: 'create'; name: string }
  | { type: 'select'; id: string };

export type ProfileScreenProfile = {
  id: string;
  name: string;
};

interface ProfileScreenProps {
  profiles: ProfileScreenProfile[];
  activeProfileId: string | null;
  onContinue: (choice: ProfileChoice) => void;
}

export default function ProfileScreen({ profiles, activeProfileId, onContinue }: ProfileScreenProps): JSX.Element {
  const defaultName = useMemo(() => {
    if (!profiles.length) return '';
    const active = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;
    return active?.id || profiles[0]?.id || '';
  }, [profiles, activeProfileId]);

  const [mode, setMode] = useState<'create' | 'select'>(profiles.length ? 'select' : 'create');
  const [newName, setNewName] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string>(defaultName);

  const canContinue = mode === 'create' ? Boolean(newName.trim()) : Boolean(selectedId);

  return (
    <div className="setup-shell">
      <div className="setup-panel">
        <div className="setup-topbar">
          <span className="setup-topbar-spacer" />

          <div className="setup-topbar-center">
            <div className="setup-brandline">
              <div className="setup-logo" aria-label="Omulongo">
                <Dices size={18} aria-hidden="true" />
                <span>omu</span>
                <span className="diamond">1♦</span>
                <span>ngo</span>
              </div>
            </div>
            <p className="setup-sub">Create your player profile to begin.</p>
          </div>

          <span className="setup-topbar-spacer" />
        </div>

        <div className="setup-body">
          <div className="setup-main" role="region" aria-label="Profile setup">
            {profiles.length ? (
              <section className="setup-group setup-wide">
                <div className="setup-label">Profile</div>
                <div className="setup-segment" role="group" aria-label="Profile mode">
                  <button className={`setup-segment-btn ${mode === 'select' ? 'active' : ''}`} type="button" onClick={() => setMode('select')}>
                    <User size={16} aria-hidden="true" />
                    <span>Select</span>
                  </button>
                  <button className={`setup-segment-btn ${mode === 'create' ? 'active' : ''}`} type="button" onClick={() => setMode('create')}>
                    <User size={16} aria-hidden="true" />
                    <span>Create</span>
                  </button>
                </div>
              </section>
            ) : null}

            {mode === 'select' && profiles.length ? (
              <section className="setup-group setup-wide">
                <div className="setup-label">Choose</div>
                <select className="form-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </section>
            ) : (
              <section className="setup-group setup-wide">
                <div className="setup-label">Name</div>
                <input
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Player name"
                  inputMode="text"
                  autoFocus
                />
              </section>
            )}
          </div>
        </div>

        <div className="setup-footer">
          <button
            className="setup-btn primary"
            type="button"
            disabled={!canContinue}
            onClick={() => {
              if (mode === 'create') onContinue({ type: 'create', name: newName });
              else onContinue({ type: 'select', id: selectedId });
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

